import React, {
  useEffect,
  useMemo,
  useState,
  useLayoutEffect
} from "react";
import { createPortal } from "react-dom";
import AddSkillForm from "./AddSkillForm";
import SkillCard from "./SkillCard";
import { skillService } from "./skillService";
import { Skill } from "./types";
import { useToaster } from "contexts/toasterContext";
import HeaderButtons from "components/Button/HeaderButtons";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import Loader from "components/Loader";
import { PROFILE_HEADER_ACTIONS_SLOT_ID } from "../headerActionsSlot";
// import { PROFILE_HEADER_ACTIONS_SLOT_ID } from "./headerActionsSlot";

export default function SkillManagement() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [search] = useState("");
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Skill | null>(null);

  const { addToast } = useToaster();

  const loadSkills = async () => {
    setLoading(true);
    try {
      const res = await skillService.getAllSkills();

      setSkills(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch {
      addToast(
        "Failed to load skills",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSkills();
  }, []);

  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    setActionsSlot(document.getElementById(PROFILE_HEADER_ACTIONS_SLOT_ID));
  }, []);

  const filteredSkills = useMemo(() => {
    return skills.filter((skill) =>
      skill.skill
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [skills, search]);

  const handleDelete = async (
    id: number
  ) => {

    try {
      await skillService.deleteSkill(id);

      addToast(
        "Skill deleted successfully",
        "success"
      );
      setDeleteModal(false);
      loadSkills();
    } catch {
      addToast(
        "Failed to delete skill",
        "error"
      );
    }
  };

  const handleUpdate = async (
    id: number,
    value: string
  ) => {
    try {
      await skillService.updateSkill(
        id,
        value
      );

      addToast(
        "Skill updated successfully",
        "success"
      );

      loadSkills();
    } catch {
      addToast(
        "Failed to update skill",
        "error"
      );
    }
  };

  const handleAddSkills = () => {
    setOpenAddModal(true);
  }

  const actionsGroup = (
    <div className="flex items-center gap-3 ml-auto">
      <HeaderButtons
        variant="primary"
        onClick={handleAddSkills}
      >
        Add Skill
      </HeaderButtons>
    </div>
  );

  const headerRow = actionsSlot ? (
    createPortal(actionsGroup, actionsSlot)
  ) : (
    <div className="flex flex-wrap items-start justify-end w-full gap-3 pt-2">{actionsGroup}</div>
  );

  return (
    <div className="space-y-4 px-4 pb-4 sm:px-6 sm:pb-6 overflow-y-auto">
      {headerRow}

      <div className="bg-white dark:bg-gray-800 pt-2">
        {loading ? (
          <Loader />
        ) : filteredSkills.length === 0 ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            No skills found
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {filteredSkills.map(
              (skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  onDelete={() => {
                    setSelectedRow(skill);
                    setDeleteModal(true);
                  }}
                  onUpdate={handleUpdate}
                />
              )
            )}
          </div>
        )}
      </div>

      {
        openAddModal && (
          <GenericModal
            isOpen={openAddModal}
            onClose={() => setOpenAddModal(false)}
            header="Add Skills"
          >
            <AddSkillForm
              onSuccess={() => {
                loadSkills();
                setOpenAddModal(false);
              }}
            />
          </GenericModal>
        )
      }
      {openDeleteModal && (
        <GenericModal
          isOpen={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          header="Delete Skill"
        >
          <DeletePopup
            handleDelete={handleDelete}
            onClose={() => setDeleteModal(false)}
            selectedRow={selectedRow}
          />
        </GenericModal>
      )}
    </div>
  );
}